import React, { useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { Typography } from '@mui/material';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import { AxiosResponse } from 'axios';
import {
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Icon } from '@iconify/react';

import KeyOutlined from '../../../assets/icons/key-outlined-icon';
import UserFirstLoginLayout from './layout/user-first-login-layout';
import * as S from './styles';
import { api } from '../../../http';
import { DefaultRes } from '../../../types';
import { AuthService } from '../../../services/auth.service';
import { passwordField } from '../../../schemas/password-policy';
import { PasswordRequirements } from '../../../components/password-requirements';
import { toast } from 'react-toastify';
import { RESPONSE_CODES } from '../../../constants/responseCodes.constants';

export const UserFirstLogin = () => {
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleShowPassword = () => {
        setShowPassword((show) => !show);
    };

    const formik = useFormik({
        initialValues: {
            confirmPassword: '',
            password: '',
        },
        validationSchema: Yup.object().shape({
            password: passwordField('Please enter your password'),
            confirmPassword: Yup
                .string().oneOf(
                [Yup.ref("password"), null],
                "Passwords must match"
                )
                .required('Please confirm your password'),
        }),
        onSubmit: async (values) => {
            setSubmitting(true);

            try {
                const { data: { isValid, message } } = await api.post<
                    DefaultRes<boolean>,
                    AxiosResponse<DefaultRes<boolean>>
                >(`/customerWebAppUser/setPassword`, { password: values.password });

                if (isValid) {
                    AuthService.dispatch.setFirstLogin(false);
                } else {
                    toast(RESPONSE_CODES[message] || RESPONSE_CODES.UNKNOWN, { autoClose: 1500, type: 'error' });
                }
            } catch (error: any) {
                // Surface server-side rejections (e.g. NIST 800-63B password
                // policy failures) instead of leaving the form stuck submitting.
                const code = error?.response?.data?.message;
                // Log only the typed code/message, not the full error object,
                // to avoid surfacing response internals in the browser console.
                console.error('[UserFirstLogin] Failed to set password:', code || error?.message || 'unknown error');
                toast(RESPONSE_CODES[code] || RESPONSE_CODES.UNKNOWN, { autoClose: 1500, type: 'error' });
            } finally {
                setSubmitting(false);
            }
        }
    });

    const { errors, touched, handleSubmit, getFieldProps } = formik;

    return (
        <UserFirstLoginLayout>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <S.Container>
                        <div className="icon-container">
                            <KeyOutlined />
                        </div>
                        <S.ForgotPasswordContainer>
                            <Typography fontWeight={500} fontSize={22}>
                                Register a new password
                            </Typography>
                            <Typography mt="10px" variant="body1" color="grayMedium.main">
                                You will need a password to access the platform, as the old one is temporary.
                            </Typography>
                            <S.FormContainer noValidate>
                                <S.FormGroup>
                                    <TextField
                                        label="New password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        variant="outlined"
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleShowPassword} edge="end">
                                                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={Boolean(touched.password && errors.password)}
                                        helperText={touched.password && errors.password}
                                        {...getFieldProps('password')}
                                    />
                                </S.FormGroup>

                                <S.FormGroup>
                                    <TextField
                                        fullWidth
                                        label="Repeat new password"
                                        type={showPassword ? "text" : "password"}
                                        variant="outlined"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleShowPassword} edge="end">
                                                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                                        helperText={touched.confirmPassword && errors.confirmPassword}
                                        {...getFieldProps('confirmPassword')}
                                    />
                                </S.FormGroup>

                                <PasswordRequirements password={formik.values.password} />

                                <LoadingButton
                                    size="large"
                                    variant="contained"
                                    fullWidth
                                    loading={submitting}
                                    onClick={() => handleSubmit()}
                                >
                                    Continue
                                </LoadingButton>
                            </S.FormContainer>
                        </S.ForgotPasswordContainer>
                    </S.Container>
                </Form>
            </FormikProvider>
        </UserFirstLoginLayout>
    )
}